import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  INTERVAL_PHASES,
  advanceInterval,
  configureInterval,
  createIntervalState,
  intervalRemainingSec,
  intervalStateFromSync,
  intervalSyncState,
  pauseInterval,
  resetInterval,
  resumeInterval,
  startInterval,
  tickInterval,
} from "../utils/intervalTimer";
import { playIntervalCue, primeIntervalAudio } from "../utils/intervalBeep";

const TICK_MS = 250;
const HEARTBEAT_MS = 5000;

/**
 * Coach-owned interval timer. Lives on the dashboard so it keeps running when
 * the panel is closed, and publishes its state to players via `liveModeSync`.
 */
export function useCoachIntervalTimer({
  teId,
  defaults,
  enabled = true,
  synced = null,
  publish = null,
}) {
  const [state, setState] = useState(() => createIntervalState(defaults));
  const [now, setNow] = useState(() => Date.now());
  const restoredTeRef = useRef(null);
  const lastCueRef = useRef({ phase: null, round: null, tick: null });
  const publishRef = useRef(publish);
  publishRef.current = publish;
  const defaultsKey = `${defaults?.workSec}|${defaults?.restSec}|${defaults?.rounds}`;

  useEffect(() => {
    setState(createIntervalState(defaults));
    restoredTeRef.current = null;
    lastCueRef.current = { phase: null, round: null, tick: null };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teId, defaultsKey]);

  // Pick up a timer already running on the server (coach reloaded the page).
  useEffect(() => {
    if (!enabled || !synced?.state || restoredTeRef.current === teId) return;
    restoredTeRef.current = teId;
    setState((cur) => {
      if (cur.phase !== INTERVAL_PHASES.IDLE) return cur;
      if (synced.state.phase === INTERVAL_PHASES.IDLE) return cur;
      return intervalStateFromSync(synced.state, synced.receivedAt, synced.ageMs) || cur;
    });
  }, [enabled, synced, teId]);

  useEffect(() => {
    if (!state.running) return undefined;
    const id = setInterval(() => {
      const t = Date.now();
      setNow(t);
      setState((cur) => tickInterval(cur, t));
    }, TICK_MS);
    return () => clearInterval(id);
  }, [state.running]);

  const remainingSec = intervalRemainingSec(state, now);

  useEffect(() => {
    const last = lastCueRef.current;
    if (last.phase !== state.phase || last.round !== state.round) {
      if (last.phase != null) {
        if (state.phase === INTERVAL_PHASES.WORK) playIntervalCue("work");
        else if (state.phase === INTERVAL_PHASES.REST) playIntervalCue("rest");
        else if (state.phase === INTERVAL_PHASES.DONE) playIntervalCue("done");
      }
      lastCueRef.current = { phase: state.phase, round: state.round, tick: null };
      return;
    }
    if (state.running && remainingSec > 0 && remainingSec <= 3 && last.tick !== remainingSec) {
      lastCueRef.current = { ...last, tick: remainingSec };
      playIntervalCue("tick");
    }
  }, [state.phase, state.round, state.running, remainingSec]);

  const stateRef = useRef(state);
  stateRef.current = state;

  const publishNow = useCallback(() => {
    if (!enabled) return;
    publishRef.current?.("interval", intervalSyncState(stateRef.current, Date.now()));
  }, [enabled]);

  useEffect(() => {
    if (restoredTeRef.current == null && state.phase === INTERVAL_PHASES.IDLE) return;
    publishNow();
  }, [state.phase, state.round, state.running, state.workSec, state.restSec, state.rounds, publishNow]);

  useEffect(() => {
    if (!state.running) return undefined;
    const id = setInterval(publishNow, HEARTBEAT_MS);
    return () => clearInterval(id);
  }, [state.running, publishNow]);

  const actions = useMemo(
    () => ({
      start: () => {
        primeIntervalAudio();
        restoredTeRef.current = restoredTeRef.current ?? teId;
        setState((cur) => startInterval(cur, Date.now()));
      },
      pause: () => setState((cur) => pauseInterval(cur, Date.now())),
      resume: () => {
        primeIntervalAudio();
        setState((cur) => resumeInterval(cur, Date.now()));
      },
      skip: () => setState((cur) => advanceInterval(cur, Date.now())),
      reset: () => setState((cur) => resetInterval(cur)),
      configure: (config) => setState((cur) => configureInterval(cur, config)),
    }),
    [teId]
  );

  return { state, remainingSec, ...actions };
}

export default useCoachIntervalTimer;
